import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { BlockSwitch } from "@/features/blocks/components/BlockSwitch";
import { FormBlocksMock } from "@/features/developer/form-anatomy/FormPageMock";
import { useNav } from "@/utils/nav";
import { RichLabel } from "@patient-watch/forms";
import type { BlockT } from "@patient-watch/types";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_app/design/forms/blocks")({
    component: FormBlocksPage,
});

function demoImage(label: string, fill: string, accent: string) {
    return `data:image/svg+xml;utf8,${encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="280" viewBox="0 0 640 280">
            <rect width="640" height="280" fill="${fill}"/>
            <circle cx="320" cy="130" r="46" fill="${accent}"/>
            <text x="320" y="250" font-family="sans-serif" font-size="18" text-anchor="middle" fill="#334155">${label}</text>
        </svg>`
    )}`;
}

const BODY_TEXT =
    "Thank you for taking part. The next few questions ask about your knee over the **last 7 days**. There are no right or wrong answers.";

function block(id: string, overrides: Partial<BlockT> & Pick<BlockT, "block_type">): BlockT {
    return {
        block_id: id,
        page_id: "demo-block-page",
        block_index: 1,
        content: null,
        media_url: null,
        caption: null,
        text_align: "left",
        text_style: null,
        ...overrides,
    };
}

interface BlockDemo {
    title: string;
    note: string;
    block: BlockT;
}

const BLOCK_DEMOS: BlockDemo[] = [
    {
        title: "text - heading",
        note: "text_style = heading",
        block: block("heading", { block_type: "text", text_style: "heading", content: "About your knee" }),
    },
    {
        title: "text - lead",
        note: "text_style = lead",
        block: block("lead", {
            block_type: "text",
            text_style: "lead",
            content: "These questions help your surgeon see how you are recovering.",
        }),
    },
    {
        title: "text - paragraph",
        note: "text_style = paragraph",
        block: block("paragraph", { block_type: "text", text_style: "paragraph", content: BODY_TEXT }),
    },
    {
        title: "text - small",
        note: "text_style = small",
        block: block("small", {
            block_type: "text",
            text_style: "small",
            content: "Your answers are stored securely and shared only with the team who invited you.",
        }),
    },
    {
        title: "text - callout",
        note: "text_style = callout, or no style next to a question",
        block: block("callout", {
            block_type: "text",
            text_style: "callout",
            content: "If your pain is getting worse quickly, contact your hospital or call 111.",
        }),
    },
    {
        title: "text - centred",
        note: "text_style = paragraph, text_align = center",
        block: block("centred", {
            block_type: "text",
            text_style: "paragraph",
            text_align: "center",
            content: "You have answered every question. Press Next to save your answers.",
        }),
    },
    {
        title: "image",
        note: "media_url and caption",
        block: block("image", {
            block_type: "image",
            media_url: demoImage("Image", "#e0e7ff", "#6366f1"),
            caption: "Figure 1. Where to measure the swelling around your knee.",
        }),
    },
    {
        title: "video",
        note: "media_url (YouTube or Vimeo) and caption",
        block: block("video", {
            block_type: "video",
            media_url: "https://www.youtube.com/watch?v=aqz-KE-bpKQ",
            caption: "Watch this short video before you start your exercises.",
        }),
    },
    {
        title: "image-carousel",
        note: "content is a JSON list of { src, alt }",
        block: block("carousel", {
            block_type: "image-carousel" as BlockT["block_type"],
            content: JSON.stringify([
                { src: demoImage("Picture A", "#e0f2fe", "#0284c7"), alt: "Picture A: knee straight" },
                { src: demoImage("Picture B", "#fce7f3", "#db2777"), alt: "Picture B: knee half bent" },
                { src: demoImage("Picture C", "#dcfce7", "#16a34a"), alt: "Picture C: knee fully bent" },
            ]),
        }),
    },
];

function DemoCard({ title, note, children }: { title: string; note: string; children: React.ReactNode }) {
    return (
        <ui.Card>
            <ui.CardHeader className="pb-2">
                <code className="text-muted-foreground text-xs">{title}</code>
                <ui.CardDescription className="text-xs">{note}</ui.CardDescription>
            </ui.CardHeader>
            <ui.CardContent>
                <div data-form-text>{children}</div>
            </ui.CardContent>
        </ui.Card>
    );
}

function FormBlocksPage() {
    useNav([
        { label: "Design", route: { to: "/design" } },
        { label: "Forms", route: { to: "/design/forms" } },
        { label: "Blocks", route: { to: "/design/forms/blocks" } },
    ]);

    return (
        <AdminPageLayout
            title="Form blocks"
            description="Content shown on a form page between questions: text in each style, images, videos and image carousels."
            maxWidthClassName="max-w-2xl"
        >
            <div className="space-y-6">
                <FormBlocksMock />
                <DemoCard title="text - page body" note="No text_style, on a page with no questions">
                    <div data-anno="block-text" className="text-slate-700 dark:text-slate-300 text-lg leading-relaxed">
                        <RichLabel>{BODY_TEXT}</RichLabel>
                    </div>
                </DemoCard>
                {BLOCK_DEMOS.map((demo) => (
                    <DemoCard key={demo.block.block_id} title={demo.title} note={demo.note}>
                        <BlockSwitch block={demo.block} />
                    </DemoCard>
                ))}
            </div>
        </AdminPageLayout>
    );
}
